import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, Outlet, useSearchParams } from "react-router-dom";
import { ChatProvider } from "./contexts/ChatContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SCHOOL_CONFIG, CSS_VARIABLES, isFeatureEnabled, getSEOData } from "./config/appConfig";
import useAuthStore from "./store/authStore";
import useUserStore from "./store/userStore";
import useSchoolStore from "./store/schoolStore";
import { getSchoolNameById } from "./utils/schoolUtils";
import AppLoadingPage from "./components/common/AppLoadingPage";
import Button from "./components/common/Button";

// Import page components
import LandingPage from "./pages/LandingPage.jsx";
import LoginPage from "./pages/LoginPage";
import AdmissionForm from "./components/AdmissionForm";
import PrivacyPolicy from "./components/PrivacyPolicy";
import AdminAgent from "./pages/AdminAgent";
import NotificationTemplate from "./components/chat/agent/NotificationTemplate";
import HPCReportWindow from "./components/hpc/HPCReportWindow";
import TimeTableWindow from "./components/time_table/TimeTableWindow";

// Import dashboard page components
import DashboardHome from "./components/dashboard/DashboardHome";
import StaffManagementPage from "./pages/user/staff/StaffManagementPage";
import StudentManagementPage from "./pages/user/student/StudentManagementPage";
import StudentDetailsPage from "./pages/user/student/StudentDetailsPage";
import StaffDetailsPage from "./pages/user/staff/StaffDetailsPage";
import AttendancePage from "./pages/AttendancePage";
import TimeTablePage from "./pages/TimeTablePage";
import SettingsPage from "./pages/SettingsPage";
import AskMePage from "./pages/AskMePage";
import DashboardLayout from "./pages/DashboardLayout";
import HPCReportPage from "./pages/hpc/HPCReportPage";
import BulkReportPage from "./pages/reports/BulkReportPage";
import StudentReportPage from "./pages/reports/StudentReportPage";
import StudentReportSubjectPage from "./pages/reports/StudentReportSubjectPage";
import LessonPlanReportPage from "./pages/reports/LessonPlanReportPage";
import ClassReportsPage from "./pages/reports/ClassReportsPage";
import Reports from "./pages/reports/Reports";
import ReportItems from "./pages/reports/ReportItems";
import FeePage from "./pages/fee/FeePage";
import Logger from './utils/logger';
import MessagePDFExportWindow from "./components/chat/export/MessagePDFExportWindow.jsx";

// School ID Extractor Component
const SchoolIdExtractor = () => {
  const location = useLocation();
  const { setSchoolId } = useSchoolStore();

  useEffect(() => {
    
    // Extract schoolId from path pattern: /dash/schoolId
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    // Check if the path follows the pattern /dash/schoolId
    if (pathSegments.length >= 2 && pathSegments[0] === 'dash') {
      const schoolIdFromPath = pathSegments[1];
      if (schoolIdFromPath && schoolIdFromPath.trim() !== '') {
        Logger.log('schoolIdFromPath', schoolIdFromPath);
        setSchoolId(schoolIdFromPath);
      }
    }
  }, [location.pathname, setSchoolId]);

  return null; // This component doesn't render anything
};

// Authentication Guard Component
const AuthGuard = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { isUserLoggedIn, userProfile, logoutUser } = useUserStore();
  const { schoolId,clearSchoolData } = useSchoolStore();
  const location = useLocation();
  
  // Show loading while checking authentication
  if (isLoading) {
    return <AppLoadingPage />;
  }
  
  const redirectUrl = encodeURIComponent(location.pathname + location.search);


        if (userProfile && userProfile.schoolId !== schoolId) {
    const redirectUrl = encodeURIComponent(location.pathname + location.search);
    logoutUser();
    clearSchoolData();
    return <Navigate to={`/dash/${schoolId}/login?redirect=${redirectUrl}`} replace />;
  }


  if (!isAuthenticated) {
    return <Navigate to={`/dash/${schoolId}/login?redirect=${redirectUrl}`} replace />;
  }
  
  if (!isUserLoggedIn) {
    return <Navigate to={`/dash/${schoolId}/login?redirect=${redirectUrl}`} replace />;
  }
  
  if (!schoolId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ui-page px-4">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-ui-ink">School not found</h1>
          <p className="mt-2 text-sm text-ui-ink-2">The link does not say which school to open. Please use the link your school gave you, or contact support.</p>
        </div>
      </div>
    );
  }

  

  
  return children;
};

// Privacy Policy Wrapper Component
const PrivacyPolicyWrapper = () => {
  const { schoolId } = useParams();
  return <PrivacyPolicy schoolName={getSchoolNameById(schoolId)} />;
};

// HPC Report Public Wrapper Component
const HPCReportPublicWrapper = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId') || searchParams.get('id');
  return <HPCReportWindow reportId={reportId} />;
};

function App() {
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  // Apply theme and SEO on app initialization
  useEffect(() => {

    // Check for app update
 
    


    const initializeApp = async () => {
      // Authentication and user profile logic should only run for dashboard routes
      const pathname = window.location.pathname;
      const isDashboardRoute = pathname.startsWith('/dash');

      try {
        let unsubscribe = null;
        
        if (isDashboardRoute) {
          // Initialize Firebase auth listener only for dashboard-related routes
          const result = await useAuthStore.getState().initAuthListener();
          unsubscribe = result.success ? result.value : null;
        }

        // Apply SEO data (remains for all routes)
        const seoData = getSEOData();
        document.title = seoData.title;
        
        // Update meta tags
        updateMetaTag('description', seoData.description);
        updateMetaTag('keywords', seoData.keywords);
        updateMetaTag('og:title', seoData.title);
        updateMetaTag('og:description', seoData.description);
        updateMetaTag('og:image', seoData.ogImage);
        updateMetaTag('og:url', seoData.canonicalUrl);
        
        // Update favicon
        updateFavicon(SCHOOL_CONFIG.branding.favicon);
        
        // Add school-specific body class
        document.body.classList.add(`school-${SCHOOL_CONFIG.id}`);

        let refreshTimeout = null;
        
        if (isDashboardRoute) {
          // Silent profile refresh for already logged-in users only for dashboard routes
          const refreshUserProfile = async () => {
            const userStore = useUserStore.getState();
            if (userStore.isUserLoggedIn && userStore.userProfile) {
              try {
                const refreshResult = await userStore.refreshUserProfile();
                if (refreshResult.success) {
                  Logger.log('User profile refreshed successfully on app startup');
                } else if (refreshResult.code !== 'USER_NOT_FOUND') {
                  Logger.warn('Silent profile refresh failed:', refreshResult.message);
                }
              } catch (error) {
                Logger.warn('Silent profile refresh error:', error.message);
              }
            }
          };

          // Call refresh with a small delay to ensure stores are initialized
          refreshTimeout = setTimeout(refreshUserProfile, 1000);
        }
        
        // Set auth initialization complete
        setIsAuthInitializing(false);
        
        // Cleanup function - normally would join with useEffect's cleanup
        // Note: The existing pattern calls initializeApp() but doesn't handle the returned cleanup from this async function.
        // We'll maintain it but keep the variables in scope for reference.
        return () => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
          if (refreshTimeout) {
            clearTimeout(refreshTimeout);
          }
          document.body.classList.remove(`school-${SCHOOL_CONFIG.id}`);
        };
      } catch (error) {
        console.error('App initialization error:', error);
        setIsAuthInitializing(false);
      }
    };

    initializeApp();
  }, []);

  // Helper function to update meta tags
  const updateMetaTag = (name, content) => {
    if (!content) return;
    
    let selector = `meta[name="${name}"]`;
    if (name.startsWith('og:')) {
      selector = `meta[property="${name}"]`;
    }
    
    let metaTag = document.querySelector(selector);
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (name.startsWith('og:')) {
        metaTag.setAttribute('property', name);
      } else {
        metaTag.setAttribute('name', name);
      }
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', content);
  };

  // Helper function to update favicon
  const updateFavicon = (faviconUrl) => {
    if (!faviconUrl) return;
    
    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('href', faviconUrl);
  };

  return (
    <ThemeProvider>
      {isAuthInitializing ? (
        <AppLoadingPage />
      ) : (
        <Router>
          <SchoolIdExtractor />
          <ChatProvider>
          <Routes>
            {/* Public Routes - No authentication required */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/dash/:schoolId/login" element={<LoginPage />} />
            <Route path="/:schoolId/privacy-policy" element={<PrivacyPolicyWrapper />} />
            <Route path="/export/message" element={<MessagePDFExportWindow />} />
            <Route path="/student-report" element={<StudentReportPage />} />
            <Route path="/lesson-plan-report" element={<LessonPlanReportPage />} />
            <Route path="/hpc-report" element={<HPCReportPublicWrapper />} />
            {/* All protected routes now under /dash/:schoolId/ */}
            
            {/* Dashboard Route with nested routes - Protected */}
            <Route 
              path="/dash/:schoolId" 
              element={
                <AuthGuard>
                  <DashboardLayout />
                </AuthGuard>
              } 
            >
              {/* Default dashboard home route */}
              <Route index element={<DashboardHome />} />

              {/* Ask Me: questions answered by the AI agent */}
              <Route path="ask" element={<AskMePage />} />
              
              {/* Student Management Routes */}
              <Route path="students" element={<StudentManagementPage />} />
              <Route path="students/:studentId" element={<StudentDetailsPage />} />
              
              {/* Staff Management Routes */}
              <Route path="staff" element={<StaffManagementPage />} />
              <Route path="staff/:staffId" element={<StaffDetailsPage />} />
              
              {/* Other Dashboard Routes */}
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="settings" element={<SettingsPage />} />
              
              {/* Feature-gated dashboard routes */}
              {isFeatureEnabled('hpcReport') && (
                <Route path="hpc-report" element={<HPCReportPage />} />
              )}
              
              {isFeatureEnabled('timeTable') && (
                <Route path="time-table" element={<TimeTablePage />} />
              )}
              
              {isFeatureEnabled('bulkReports') && (
                <Route path="bulk-reports" element={<BulkReportPage />} />
              )}
              
              {/* Reports Dashboard Hub & Modules */}
              <Route path="reports" element={<Reports />} />
              <Route path="reports/class" element={<ClassReportsPage />} />
              <Route path="reports/:reportId/items" element={<ReportItems />} />
              <Route path="reports/lesson-plans" element={<LessonPlanReportPage />} />
              <Route path="lesson-plan-report" element={<LessonPlanReportPage />} />
              <Route path="student-report" element={<StudentReportPage />} />
              <Route path="student-report/subject" element={<StudentReportSubjectPage />} />
              
              {/* Fee Management */}
              <Route path="fees" element={<FeePage />} />
              
              {/* Admission Form - Feature gated */}
              {isFeatureEnabled('admissionForm') && (
                <Route 
                  path="admission" 
                  element={
                    <AdmissionForm 
                      title={`${SCHOOL_CONFIG.name} Admission Form`}
                      formUrl={SCHOOL_CONFIG.externalLinks.admissionForm}
                      formTitle={SCHOOL_CONFIG.name}
                    />
                  } 
                />
              )}

              {/* AI Chat Admin - Feature gated */}
              {isFeatureEnabled('aiChat') && (
                <Route path="agent-admin" element={<AdminAgent />} />
              )}

              {/* Notifications - Feature gated */}
              {isFeatureEnabled('notifications') && (
                <Route 
                  path="notification" 
                  element={
                    <NotificationTemplate 
                      type={new URLSearchParams(window.location.search).get('type')}
                    />
                  } 
                />
              )}
            </Route>

            {/* Attendance route */}
            <Route path="/attendance" element={<AttendancePage />} />

            {/* Legacy routes without schoolId - redirect to login */}
            <Route path="/dash" element={<Navigate to="/login" replace />} />
            <Route path="/admission" element={<Navigate to="/login" replace />} />
            <Route path="/agent-admin" element={<Navigate to="/login" replace />} />
            <Route path="/notification" element={<Navigate to="/login" replace />} />
            <Route path="/time-table" element={<Navigate to="/login" replace />} />

            {/* 404 Page */}
            <Route 
              path="*" 
              element={
                <div className="flex min-h-screen items-center justify-center bg-ui-page px-4">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-brand-600">404</p>
                    <h1 className="mt-1 text-2xl font-semibold text-ui-ink">Page not found</h1>
                    <p className="mt-2 text-sm text-ui-ink-2">The address may be mistyped, or the page has moved.</p>
                    <Button href="/" className="mt-5">Go to the home page</Button>
                  </div>
                </div>
              } 
            />
          </Routes>
        </ChatProvider>
      </Router>
      )}
    </ThemeProvider>
  );
}

export default App;